import type { BankTransaction } from '@/types/banking'
import dayjs from '@dcp/shared/utils/dayjs'
import { downloadBlob } from './downloadBlob'

/**
 * Escape CSV field value
 * Wraps in quotes if contains comma, newline, or quote
 */
function escapeCsvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue = String(value)

  // If contains comma, newline, or quote, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

/**
 * Format date for CSV export
 */
function formatDateForCsv(dateStr: string): string {
  const d = dayjs(dateStr)
  if (!d.isValid()) return ''
  return d.format('MM/DD/YYYY')
}

/**
 * Format amount for CSV export
 */
function formatAmountForCsv(amount: number): string {
  return amount.toFixed(2)
}

/**
 * Convert bank transactions to CSV string
 */
export function transactionsToCsv(transactions: BankTransaction[]): string {
  // CSV Headers
  const headers = [
    'Facility',
    'Date',
    'Account Number',
    'Account Name',
    'Category',
    'Amount',
    'Description',
    'Memo',
    'API Status',
  ]

  // Build CSV rows
  const rows = transactions.map((txn) => [
    escapeCsvField(txn.facility),
    escapeCsvField(formatDateForCsv(txn.date)),
    escapeCsvField(txn.accountNumber),
    escapeCsvField(txn.accountName),
    escapeCsvField(txn.category),
    escapeCsvField(formatAmountForCsv(txn.amount)),
    escapeCsvField(txn.description),
    escapeCsvField(txn.memo),
    // Mirrors the table's "API Status" column (DCP-344): manual entries and
    // file imports have no API origin, so they export as "-" instead of the
    // 'pending' that their untouched sync_status DEFAULT used to leak here.
    // API rows keep exporting the raw enum value, not the badge label, so
    // existing consumers of this file see no change for them.
    escapeCsvField(txn.apiStatus ?? '-'),
  ])

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n')

  return csvContent
}

/**
 * Download CSV file with bank transactions
 */
export function downloadTransactionsCsv(transactions: BankTransaction[], filename?: string): void {
  const csvContent = transactionsToCsv(transactions)

  // Create blob with BOM for Excel compatibility
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })

  downloadBlob(blob, filename || `transactions_${dayjs().format('YYYY-MM-DD')}.csv`)
}
